import { useState, useCallback } from "react";
import { ServerFlightInfo } from "@/types/flights";
import { Trip, TransportData, TripTransport } from "@/types/trip";
import useCurrencyStore from "@/stores/ui/currencyStore";
type typeOfUpdateTripData = (tripData: Trip) => void;

export const useTransportManager = (updateTripData: typeOfUpdateTripData) => {
  const { selectedCurrency } = useCurrencyStore();
  const [selectedFlight, setSelectedFlight] = useState<TripTransport | null>(
    null
  );
  const [addedFlight, setAddedFlight] = useState<TripTransport | null>(null);
  const [flightModalOpen, setFlightModalOpen] = useState(false);

  const addOrReplaceFlightInTrip = useCallback(
    (trip: Trip, flight: ServerFlightInfo, transportData: TransportData) => {
      const fromLocationId = transportData.locationAbove?.id || "";
      const toLocationId = transportData.locationBelow?.id || "";

      const updatedTrip = { ...trip };

      // Check if a transport already exists for this route
      const existingTransportIndex = updatedTrip.transport.findIndex(
        (t) =>
          t.fromLocationId === fromLocationId && t.toLocationId === toLocationId
      );

      const newTransport: TripTransport = {
        id: flight.id,
        fromLocationId,
        toLocationId,
        type: "api",
        legs:
          flight.route?.map((routeLeg) => ({
            id: `${flight.id}-${routeLeg.flight_number}`,
            mode: "flight",
            provider: {
              name: routeLeg.airline || "Unknown Airline",
              code: routeLeg.short_airline || "UNK",
              type: "airline",
              logo: `https://images.kiwi.com/airlines/64x64/${routeLeg.short_airline}.png`,
            },
            departure: {
              location: {
                name: routeLeg.from.city,
                code: routeLeg.from.airport,
                coordinates: {
                  latitude: parseFloat(routeLeg.from.latitude),
                  longitude: parseFloat(routeLeg.from.longitude),
                },
                timezone: "UTC",
              },
              time: {
                utc: routeLeg.departure_utc,
                local: routeLeg.departure_local,
              },
            },
            arrival: {
              location: {
                name: routeLeg.to.city,
                code: routeLeg.to.airport,
                coordinates: {
                  latitude: parseFloat(routeLeg.to.latitude),
                  longitude: parseFloat(routeLeg.to.longitude),
                },
                timezone: "UTC",
              },
              time: {
                utc: routeLeg.arrival_utc,
                local: routeLeg.arrival_local,
              },
            },
            reference: routeLeg.flight_number || "",
            duration: routeLeg.duration,
          })) || [],
        total_price: flight.price,
        currency: selectedCurrency.code,
        metadata: {
          api_booking_token: flight.booking_token,
          api_search_id: flight.id,
          api_booking_link: flight.booking_link,
        },
      };

      if (existingTransportIndex !== -1) {
        // Replace existing transport
        updatedTrip.transport[existingTransportIndex] = newTransport;
      } else {
        // Add new transport
        updatedTrip.transport.push(newTransport);
      }

      updateTripData(updatedTrip);
      setAddedFlight(newTransport);
    },
    [updateTripData, selectedCurrency]
  );

  const deleteFlightFromTrip = useCallback(
    (trip: Trip, transportData: TransportData) => {
      const fromLocationId = transportData.locationAbove?.id || "";
      const toLocationId = transportData.locationBelow?.id || "";

      const updatedTrip = { ...trip };

      updatedTrip.transport = updatedTrip.transport.filter(
        (t) =>
          !(
            t.fromLocationId === fromLocationId &&
            t.toLocationId === toLocationId
          )
      );

      updateTripData(updatedTrip);
      setAddedFlight(null);
    },
    [updateTripData]
  );

  return {
    flightModalOpen,
    setFlightModalOpen,
    selectedFlight,
    setSelectedFlight,
    addedFlight,
    setAddedFlight,
    addOrReplaceFlightInTrip,
    deleteFlightFromTrip,
  };
};
